import { WallLateralDatumAnchorAll, lateralDatumInwardWallLayers } from "building-types/datums/datumsTypes";
import { WallHorizontalDatum } from "building-types/datums/datumsTypesEnum";
import type {
  HorizontalEdgePlacementMatchExpr,
  HorizontalEdgePlacementIdentity,
  LateralEdgePlacement,
} from "building-types/entities/edgePlacementTypes";
import type { RoomIndex } from "building-types/rooms/roomIndex";
import type { RoomOutlines } from "building-types/rooms/roomOutlineTypes";
import type { RoomSet } from "building-types/rooms/roomTraversalTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { WallType } from "building-types/walls/wallEnums";
import { WallLayerType } from "building-types/walls/wallEnums";
import type { WallMassSystem } from "building-types/walls/wallSytemTypes";
import type { WallMass, Wall, WallLayer } from "building-types/walls/wallTypes";
import { WallLOD } from "building-types/walls/wallTypes";
import type {
  EdgeLayerTable,
  WallLayerExtents,
  WallLayerExtentsCache,
} from "doc/elements/openings/openingWallLayersTypes";
import { wallLateralDatumAnchorToWallLayer } from "doc/elements/wallLateralDatumAnchorToWallLayer";
import { roomEdgeOffsetFromOutline } from "doc/rooms/roomOutlineIntersection";
import { wallLayerOutlineFromLateralDatum } from "doc/rooms/roomOutlineUtils";
import { closestParamLine2 } from "geom/closestPoint";
import { DIST_EPSILON } from "geom/core";
import type { PlanarGraph } from "geom/planarGraph";
import type { EdgeRef } from "geom/polygonPlanarGraph";
import {
  polyLine2EdgeCount,
  polyLine2EdgeEnd,
  polyLine2EdgeEval,
  polyLine2EdgeNormal,
  polyLine2EdgeStart,
  polyLine2End,
  polyLine2Start,
} from "geom/polyLine2";
import { vec2Dist, vec2DistSquared, vec2Dot, vec2IsInEdgeBounds } from "geom/vec2";
import { cutGapsIntoInterval } from "../../utils/intervals";
import { wallLayerWithFallback, wallMassLayers } from "../walls/wallLayers";

export function getSubWalls(roomIndex: RoomIndex, room: Room, edgeIndex: number) {
  const roomIndexOnLevel = roomIndex.roomLevelIndices[room.id];
  const polygonPlanarGraph = roomIndex.planarGraphs[room.level.id];
  const subwalls = polygonPlanarGraph.polygonToPlanarGraph[roomIndexOnLevel][edgeIndex];
  return subwalls;
}

export function getWallMassFromEdgeRef(
  edgeRef: EdgeRef,
  planarGraph: PlanarGraph,
  wallMassSystem: WallMassSystem,
): WallMass {
  const { index, edgeIndex } = edgeRef;
  const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];
  const wallId = wallMassSystem.halfEdgeToWall[halfEdgeIndex];
  return wallMassSystem.walls[wallId];
}

/**
 * Determines extents of various wall layers in order to determine where openings can be placed
 * along a room edge. Often, a room edge must composite multiple walls and placement must take
 * into account gaps caused by perpendicular walls on the outside of the room edge.
 *```
 *                                                    Intervals Where
 *      -------------------|--------- Room Edge Min   Openings Can Fit
 *      ----------------+  |  ------- Structure Min
 *      ---------------+|  |  |------ Substrate Min   2.25"+-+ Set By Greatest
 *                     ||  |  ||                           | | Minimum (Substrate)
 *                     ||Wall ||                           | |
 *                     || A|  ||                           | |
 *                     ||  |  ||                           | |
 *           Room A    ||  |  ||    Room Edge Winding      | |
 *                     ||  |  ||    Is CCW                 | |
 *                     ||  |  ||     |                     | |
 *                     ||  |  ||     ▼                     | |
 *                     ||  |  ||                           | |
 *                     ||  |  ||                           | |
 *                     ||  |  ||                           | |
 *      ---------------+|  |  ||  Room C             28.75"+-+
 *      ----------------+  |  ||                               Gap From Wall C
 *      ---Wall C-----------  ||                               Out-To-Out Of
 *      ----------------+  |  ||                               Outermost Layers
 *      ---------------+|  |  ||                     32.25"+-+
 *                     ||  |  ||                           | |
 *                     ||  |  ||                           | |
 *                     ||Wall ||                           | |
 *                     || B|  ||                           | |
 *           Room B    ||  |  ||                           | |
 *                     ||  |  ||                           | | Set By Least
 *                     ||  |  |-----  Substrate Max  47.75"+-+ Maximum (Substrate)
 *                     ||  |  ------  Structure Max
 *                     ||  |--------  Room Edge Max
 *```
 */
export function getWallLayerExtents(
  roomIndex: RoomIndex,
  wallMassSystem: WallMassSystem,
  room: Room,
  edgeIndex: number,
): WallLayerExtents {
  const roomIndexOnLevel = roomIndex.roomLevelIndices[room.id];
  const polygonPlanarGraph = roomIndex.planarGraphs[room.level.id];
  const planarGraph = polygonPlanarGraph.planarGraph;
  const subwalls = polygonPlanarGraph.polygonToPlanarGraph[roomIndexOnLevel][edgeIndex];

  let min: number = Number.MAX_SAFE_INTEGER;
  let max: number = Number.MIN_SAFE_INTEGER;

  const edgeMinTable: EdgeLayerTable = {};
  const edgeMaxTable: EdgeLayerTable = {};
  const insideOffsetTable: EdgeLayerTable = {};
  const outsideOffsetTable: EdgeLayerTable = {};

  const gaps: number[] = [];

  const [roomEdgeStartX, roomEdgeStartY] = polyLine2EdgeStart(room.outline, edgeIndex);
  const [roomEdgeEndX, roomEdgeEndY] = polyLine2EdgeEnd(room.outline, edgeIndex);
  const roomEdgeLength = vec2Dist(roomEdgeStartX, roomEdgeStartY, roomEdgeEndX, roomEdgeEndY);

  let mostInwardLayerIntoRoom: WallLayerType | undefined = undefined;
  let mostOutwardLayerOutsideRoom: WallLayerType | undefined = undefined;

  let firstInsideOffset: number | undefined = undefined;
  let firstOutsideOffset: number | undefined = undefined;
  let lastInsideOffset: number | undefined = undefined;
  let lastOutsideOffset: number | undefined = undefined;

  let largestInsideOffset: number | undefined = undefined;
  let largestOutsideOffset: number | undefined = undefined;

  let previousInsideOffset: number | undefined = undefined;
  let previousOutsideOffset: number | undefined = undefined;

  const paramsWhereThicknessChanges: number[] = [];

  /**
   * Subwalls constitute the multiple walls that make up a single room edge. A single room edge is broken into subwalls
   * when the opposite side is divided among multiple rooms. Subwall arrays are ordered counter-clockwise around the room following
   * the winding of the room outline associated with the planar graph face.
   */
  for (let subWallIndex = 0, subWallCount = subwalls?.length; subWallIndex < subWallCount; subWallIndex++) {
    const { index, edgeIndex } = subwalls[subWallIndex];
    const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];
    const wallId = wallMassSystem.halfEdgeToWall[halfEdgeIndex];
    const wall = wallMassSystem.walls[wallId];

    // for void walls that don't have a thickness
    let insideOffset = 0;
    let outsideOffset = 0;

    if (wall.wallType === WallType.SOLID_MASS) {
      const leftIsRoom = wall?.path?.leftRoom?.id === room.id;

      let outsideGreatestMinParam = Number.MIN_SAFE_INTEGER;
      let outsideLeastMaxParam = Number.MAX_SAFE_INTEGER;

      if (subWallIndex === 0) {
        firstInsideOffset = leftIsRoom ? wall.leftOffset : wall.rightOffset;
        firstOutsideOffset = !leftIsRoom ? wall.leftOffset : wall.rightOffset;
      }

      // set each time, so we don't have to predict which wall will be the last
      lastInsideOffset = leftIsRoom ? wall.leftOffset : wall.rightOffset;
      lastOutsideOffset = !leftIsRoom ? wall.leftOffset : wall.rightOffset;

      insideOffset = leftIsRoom ? wall.leftOffset : wall.rightOffset;
      outsideOffset = !leftIsRoom ? wall.leftOffset : wall.rightOffset;

      /**
       * WallLayerType.FINISH layers are a temporary data structure
       * that should not be used outside of the wall mass generation stage. It will eventually not be accessible
       * to other parts of the system.
       *
       * @TODO - should this ultimately use LOD2 Layers? Right now that produces baseline changes
       */
      const layerTypes = [WallLayerType.STRUCTURE, WallLayerType.SUBSTRATE, WallLayerType.MASS];
      const layers: WallLayer[] = [];
      for (const type of layerTypes) {
        layers.push(...(wallMassLayers(wall, type, WallLOD.L1) as WallLayer[]));
      }

      for (const layer of layers) {
        const { wallLayerType, wallType } = layer;

        if (wallType === WallType.VOID) {
          /**
           * For the time being, we omit void wall layers from layer extents information. This is simply to
           * mimic legacy behavior where there were no void wall layers until a dedicated effort to adress them is in place.
           */
          continue;
        }

        const insideLayerOffset = leftIsRoom ? layer.leftOffset : layer.rightOffset;
        const outsideLayerOffset = !leftIsRoom ? layer.leftOffset : layer.rightOffset;

        /**
         * We store the largest inside offset so that we can position openings by this most inward layer to the room.
         * But this poses a danger, since layers (a developer might add at a later date) can be targeted by the same user selection of the
         * `OUTSIDE_OF_SURFACE` opening placement mode. This is useful when we want to place an opening by the absolute most inward to room
         * layer of a wall.
         */
        if (largestInsideOffset === undefined || insideLayerOffset > largestInsideOffset) {
          mostInwardLayerIntoRoom = layer.wallLayerType;
          largestInsideOffset = insideLayerOffset;
        }

        if (largestOutsideOffset === undefined || outsideLayerOffset > largestOutsideOffset) {
          mostOutwardLayerOutsideRoom = layer.wallLayerType;
          largestOutsideOffset = outsideLayerOffset;
        }

        /**
         * A check to confirm the wall thickness and offsets are the same between sub walls.
         * If they are not, then the wall is not continuous we need to insert a zero-length
         * gap to prevent openings from being placed here.
         */
        if (
          (previousInsideOffset !== undefined && Math.abs(insideOffset - previousInsideOffset) > DIST_EPSILON) ||
          (previousOutsideOffset !== undefined && Math.abs(outsideOffset - previousOutsideOffset) > DIST_EPSILON)
        ) {
          const backEdge = leftIsRoom ? layer.rightEdge : layer.leftEdge;
          const [bsx, bsy] = polyLine2Start(backEdge);
          const thicknessChangeParamFromBackEdge = closestParamLine2(
            roomEdgeStartX,
            roomEdgeStartY,
            roomEdgeEndX,
            roomEdgeEndY,
            bsx,
            bsy,
            false,
          );
          paramsWhereThicknessChanges.push(thicknessChangeParamFromBackEdge);
        }

        const layerOutsideEdge = leftIsRoom ? layer.rightEdge : layer.leftEdge;
        const [bsx, bsy] = polyLine2Start(layerOutsideEdge);
        const [bex, bey] = polyLine2End(layerOutsideEdge);
        const outsideLayerParamA = closestParamLine2(
          roomEdgeStartX,
          roomEdgeStartY,
          roomEdgeEndX,
          roomEdgeEndY,
          bsx,
          bsy,
          false,
        );
        const outsideLayerParamB = closestParamLine2(
          roomEdgeStartX,
          roomEdgeStartY,
          roomEdgeEndX,
          roomEdgeEndY,
          bex,
          bey,
          false,
        );

        // We need to know the min and max param of the outside edge to know where placement gaps should be.
        const outsideLayerMinParam = Math.min(outsideLayerParamA, outsideLayerParamB);
        const outsideLayerMaxParam = Math.max(outsideLayerParamA, outsideLayerParamB);

        if (outsideLayerMinParam > outsideGreatestMinParam) {
          outsideGreatestMinParam = outsideLayerMinParam;
        }

        if (outsideLayerMaxParam < outsideLeastMaxParam) {
          outsideLeastMaxParam = outsideLayerMaxParam;
        }

        if (wallLayerType === WallLayerType.MASS) {
          continue;
        }

        const layerInsideEdge = leftIsRoom ? layer.leftEdge : layer.rightEdge;
        const [sx, sy] = polyLine2Start(layerInsideEdge);
        const [ex, ey] = polyLine2End(layerInsideEdge);

        const insideLayerParamA = closestParamLine2(
          roomEdgeStartX,
          roomEdgeStartY,
          roomEdgeEndX,
          roomEdgeEndY,
          sx,
          sy,
          false,
        );
        const insideLayerParamB = closestParamLine2(
          roomEdgeStartX,
          roomEdgeStartY,
          roomEdgeEndX,
          roomEdgeEndY,
          ex,
          ey,
          false,
        );

        const paramMin = Math.min(insideLayerParamA, insideLayerParamB);
        const paramMax = Math.max(insideLayerParamA, insideLayerParamB);

        if (paramMin < min) {
          min = paramMin;
        }
        if (paramMax > max) {
          max = paramMax;
        }
        if (edgeMinTable[wallLayerType] === undefined || paramMin < edgeMinTable[wallLayerType]) {
          edgeMinTable[wallLayerType] = paramMin;
        }
        if (edgeMaxTable[wallLayerType] === undefined || paramMax > edgeMaxTable[wallLayerType]) {
          edgeMaxTable[wallLayerType] = paramMax;
        }
        if (insideOffsetTable[wallLayerType] === undefined || insideLayerOffset < insideOffsetTable[wallLayerType]) {
          insideOffsetTable[wallLayerType] = insideLayerOffset;
        }
        if (outsideOffsetTable[wallLayerType] === undefined || outsideLayerOffset > outsideOffsetTable[wallLayerType]) {
          outsideOffsetTable[wallLayerType] = outsideLayerOffset;
        }
      }

      gaps.push(outsideGreatestMinParam, outsideLeastMaxParam);
    }
    previousInsideOffset = insideOffset;
    previousOutsideOffset = outsideOffset;
  }

  // determine the greatest minimum as the minimum param an opening can be placed at
  let greatestMin = min;
  for (const key in edgeMinTable) {
    const layerMin = edgeMinTable[key];
    if (layerMin !== undefined && layerMin > greatestMin) {
      greatestMin = layerMin;
    }
  }

  // determine the least maximum as the maximmum param an opening can be placed at
  let leastMax = max;
  for (const key in edgeMaxTable) {
    const layerMax = edgeMaxTable[key];
    if (layerMax !== undefined && layerMax < leastMax) {
      leastMax = layerMax;
    }
  }

  // if greatest min is greater than the first gap number then we don't need a gap at the beginning
  if (greatestMin > gaps[0]) {
    gaps.shift();
  } else {
    gaps.unshift(greatestMin);
  }

  // if least max is less than the last gap number then we don't need a gap at the end
  if (leastMax < gaps[gaps.length - 1]) {
    gaps.pop();
  } else {
    gaps.push(leastMax);
  }

  // if each gap length is less than tolerance snap the gap second value to the first value
  for (let i = 0; i < gaps.length; i += 2) {
    const gapStart = gaps[i];
    const gapEnd = gaps[i + 1];
    if (gapEnd - gapStart < DIST_EPSILON) {
      gaps.splice(i, 2);
      i -= 2;
    }
  }

  let intervalsWhereAnOpeningCanFit = [greatestMin, leastMax];

  if (gaps.length) {
    intervalsWhereAnOpeningCanFit = cutGapsIntoInterval(greatestMin, leastMax, gaps);
  } else if (greatestMin === Number.MAX_SAFE_INTEGER || leastMax === Number.MIN_SAFE_INTEGER) {
    intervalsWhereAnOpeningCanFit = [];
  }

  // add zero-length gaps where the wall thickness changes
  if (paramsWhereThicknessChanges.length) {
    for (let i = 0; i < intervalsWhereAnOpeningCanFit.length - 1; i += 2) {
      const start = intervalsWhereAnOpeningCanFit[i];
      const end = intervalsWhereAnOpeningCanFit[i + 1];
      for (const param of paramsWhereThicknessChanges) {
        if (param > start && param < end) {
          // removes the start and end of the interval and re-inserts them with the new zero-length gap.
          intervalsWhereAnOpeningCanFit.splice(i, 2, start, param, param, end);
          break;
        }
      }
    }
  }

  return {
    edgeMinTable,
    edgeMaxTable,
    largestInsideOffset,
    largestOutsideOffset,
    mostInwardLayerIntoRoom,
    mostOutwardLayerOutsideRoom,
    intervalsWhereAnOpeningCanFit,
    roomEdgeLength,
    firstInsideOffset,
    lastInsideOffset,
    firstOutsideOffset,
    lastOutsideOffset,
    insideOffsetTable,
    outsideOffsetTable,
  };
}

export function getWallLayerOffset(
  offsetTable: EdgeLayerTable,
  thickerLayer: WallLayerType | undefined,
  horizontalDatum: WallHorizontalDatum,
): number {
  const layer = horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE ? thickerLayer : horizontalDatum;
  if (layer === undefined) {
    return 0;
  }

  return offsetTable[layer] ?? 0;
}

export function layerMinAndMaxFromSpecificWallLayer({
  placement,
  largestOffset,
  edgeLength,
  mostInwardLayerIntoRoom,
  edgeMinTable,
  edgeMaxTable,
}: {
  placement: HorizontalEdgePlacementIdentity;
  largestOffset: number | undefined;
  edgeLength: number;
  mostInwardLayerIntoRoom: WallLayerType | undefined;
  edgeMinTable: EdgeLayerTable;
  edgeMaxTable: EdgeLayerTable;
}): { layerLength: number; layerMin: number; layerMax: number } {
  if (
    placement.horizontalDatum === WallHorizontalDatum.ROOM_EDGE ||
    (placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE && largestOffset === undefined)
  ) {
    return { layerLength: edgeLength, layerMin: 0, layerMax: edgeLength };
  }

  const layer =
    placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE
      ? mostInwardLayerIntoRoom
      : placement.horizontalDatum;

  if (layer === undefined) {
    return { layerLength: edgeLength, layerMin: 0, layerMax: edgeLength };
  }

  const layerMin = edgeMinTable[layer];
  const layerMax = edgeMaxTable[layer];

  if (layerMin === undefined || layerMax === undefined) {
    return { layerLength: edgeLength, layerMin: 0, layerMax: edgeLength };
  }

  const layerLength = layerMax - layerMin;

  return { layerLength, layerMin, layerMax };
}

export function edgeLengthFromSpecificWallLayer({
  placement,
  largestOffset,
  edgeLength,
  mostInwardLayerIntoRoom,
  edgeMinTable,
  edgeMaxTable,
}: {
  placement: Pick<HorizontalEdgePlacementMatchExpr, "edgeId" | "horizontalDatum">;
  largestOffset: number | undefined;
  edgeLength: number;
  mirror: boolean;
  mostInwardLayerIntoRoom: WallLayerType | undefined;
  edgeMinTable: EdgeLayerTable;
  edgeMaxTable: EdgeLayerTable;
}): number {
  if (
    placement.horizontalDatum === WallHorizontalDatum.ROOM_EDGE ||
    (placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE && largestOffset === undefined)
  ) {
    return edgeLength;
  }

  const layer =
    placement.horizontalDatum === WallHorizontalDatum.OUTSIDE_OF_SURFACE
      ? mostInwardLayerIntoRoom
      : placement.horizontalDatum;

  if (layer === undefined) {
    return edgeLength;
  }

  const layerMin = edgeMinTable[layer];
  const layerMax = edgeMaxTable[layer];

  if (layerMin === undefined || layerMax === undefined) {
    return edgeLength;
  }

  const layerLength = layerMax - layerMin;

  return layerLength;
}

export function wallLayerOffsetAtRoomEdgeParam(
  room: Room,
  edgeIndex: number,
  resolvedParam: number,
  wallMassSystem: WallMassSystem,
  lateralDatum: Exclude<WallLateralDatumAnchorAll, "ROOM_EDGE" | "WALL_OFFSET">,
): number {
  const [x, y] = polyLine2EdgeEval(room.outline, edgeIndex, resolvedParam, false);
  const wall = getNearestWall(wallMassSystem, room, edgeIndex, x, y);
  if (!wall) {
    return 0;
  }

  const wallMass = wallLayerWithFallback({ wall, wallLayer: wallLateralDatumAnchorToWallLayer[lateralDatum] });

  const isInwardWallLayer = lateralDatumInwardWallLayers[lateralDatum];

  if (wallMass?.path?.leftRoom?.id === room.id) {
    return isInwardWallLayer ? wallMass.leftOffset : -wallMass.rightOffset;
  }
  return isInwardWallLayer ? wallMass.rightOffset : -wallMass.leftOffset;
}

export function wallDatumOffsetAtRoomEdgeParam(
  resolvedParam: number,
  edgeIndex: number,
  room: Room,
  wallMassSystem: WallMassSystem,
  lateralDatum: WallLateralDatumAnchorAll,
): number {
  if (lateralDatum === WallLateralDatumAnchorAll.ROOM_EDGE) {
    return 0;
  }

  if (lateralDatum === WallLateralDatumAnchorAll.WALL_OFFSET) {
    return getLargestInwardsWallLayerOffsetOnRoomEdge(wallMassSystem, room, edgeIndex);
  }

  return wallLayerOffsetAtRoomEdgeParam(room, edgeIndex, resolvedParam, wallMassSystem, lateralDatum);
}

/**
 * Returns a resolved lateral offset distance from the room edge. Normalization
 * is only taken into consideration for non-outward datums since they do not have
 * room outlines to intersect.
 */
export function entityLateralOffsetFromWallDatum({
  resolvedParam,
  placement,
  edgeIndex,
  room,
  roomOutlines,
  wallMassSystem,
}: {
  resolvedParam: number;
  placement: LateralEdgePlacement;
  edgeIndex: number;
  room: Room;
  roomOutlines: RoomOutlines;
  wallMassSystem: WallMassSystem;
}): number {
  const { lateralDatum, lateralOffset = 0, lateralOffsetNormalized } = placement;

  if (lateralDatum === WallLateralDatumAnchorAll.ROOM_EDGE && !lateralOffsetNormalized) {
    return lateralOffset;
  }

  const datumOffset = wallDatumOffsetAtRoomEdgeParam(resolvedParam, edgeIndex, room, wallMassSystem, lateralDatum);

  if (lateralDatum === WallLateralDatumAnchorAll.WALL_OFFSET && !lateralOffsetNormalized) {
    return lateralOffset + datumOffset;
  }

  const isInwardWallLayer = lateralDatumInwardWallLayers[lateralDatum];

  // special case where an intersection with the room outline is needed for normalized inward offsets
  if (
    (isInwardWallLayer && lateralOffsetNormalized) ||
    lateralDatum === WallLateralDatumAnchorAll.ROOM_EDGE ||
    lateralDatum === WallLateralDatumAnchorAll.WALL_OFFSET
  ) {
    const wallLayerOutline = wallLayerOutlineFromLateralDatum(room, roomOutlines, placement.lateralDatum);
    return roomEdgeOffsetFromOutline(room, edgeIndex, resolvedParam, wallLayerOutline, placement, datumOffset);
  }

  // normalization is ignored for outward datums since there is no reference length (an offset value of 0.5 will be 0.5 inches)
  return lateralOffset + datumOffset;
}

function wallMassFromEdgeRef(subWall: EdgeRef, planarGraph: PlanarGraph, wallMassSystem: WallMassSystem): WallMass {
  const { index, edgeIndex } = subWall;
  const halfEdgeIndex = planarGraph.faceHalfEdges[index][edgeIndex];
  const wallId = wallMassSystem.halfEdgeToWall[halfEdgeIndex];
  return wallMassSystem.walls[wallId];
}

/**
 * Returns the nearest subwall to a given point on a given room edge.
 */
export function getNearestWall(wallMassSystem: WallMassSystem, room: Room, edgeIndex: number, x: number, y: number) {
  const wallIndex = wallMassSystem.wallIndex;
  const roomIndexOnLevel = wallIndex.roomLevelIndices[room.id];
  const polygonPlanarGraph = wallIndex.planarGraph;
  const planarGraph = polygonPlanarGraph.planarGraph;
  const subwalls = polygonPlanarGraph.polygonToPlanarGraph[roomIndexOnLevel][edgeIndex];

  let nearestWall: Wall | undefined;
  let distance: number = Number.POSITIVE_INFINITY;

  for (let i = 0; i < subwalls.length; i++) {
    const wall = wallMassFromEdgeRef(subwalls[i], planarGraph, wallMassSystem);
    const { startX, startY, endX, endY } = wall.path;

    const edgeBound = vec2IsInEdgeBounds(x, y, startX, startY, endX, endY);

    if (!edgeBound) {
      const distSqr = vec2DistSquared(x, y, startX, startY);
      if (distSqr < distance || nearestWall === undefined) {
        distance = distSqr;
        nearestWall = wall;
      }
      continue;
    } else {
      nearestWall = wall;
      break;
    }
  }

  return nearestWall;
}

export function getLargestInwardsWallLayerOffsetOnRoomEdge(
  wallMassSystem: WallMassSystem,
  room: Room,
  edgeIndex: number,
): number {
  const { wallIndex } = wallMassSystem;
  const { roomLevelIndices, planarGraph: polygonPlanarGraph } = wallIndex;
  const roomIndexOnLevel = roomLevelIndices[room.id];
  const planarGraph = polygonPlanarGraph.planarGraph;

  const subwalls = polygonPlanarGraph.polygonToPlanarGraph[roomIndexOnLevel][edgeIndex];
  const firstWall = wallMassFromEdgeRef(subwalls[0], planarGraph, wallMassSystem);

  const getWallOffset = (wall: WallMass): number => {
    let offset = 0;
    if (wall.wallType !== WallType.VOID) {
      offset = wall?.path?.leftRoom?.id === room.id ? wall.leftOffset : wall.rightOffset;
    }
    return offset;
  };

  let maxInwardsOffset = getWallOffset(firstWall);

  for (let i = 0; i < subwalls.length; i++) {
    const wall = wallMassFromEdgeRef(subwalls[i], planarGraph, wallMassSystem);
    const offset = getWallOffset(wall);

    if (offset > maxInwardsOffset) {
      maxInwardsOffset = offset;
    }
  }

  return maxInwardsOffset;
}

/**
 * TODO(pscrugham): once wall layer extents are cached globally, this can be replaced with a simple lookup to see if a datum
 * is available to use.
 */
export function isAnySubWallSolid(roomIndex: RoomIndex, wallMassSystem: WallMassSystem, room: Room, edgeId: string) {
  const edgeIndex = room.edgeIds.indexOf(edgeId);
  if (edgeIndex === -1) {
    return false;
  }
  const { planarGraph } = roomIndex.planarGraphs[room.level.id];
  const subwalls = getSubWalls(roomIndex, room, edgeIndex);

  for (let i = 0; i < subwalls?.length; i++) {
    const wall = getWallMassFromEdgeRef(subwalls[i], planarGraph, wallMassSystem);
    if (wall.wallType !== WallType.VOID) {
      return true;
    }
  }

  return false;
}

/**
 * Since wall layer extents are expensive to compute, we cache them here. Often, the same wall will be
 * used for multiple openings, so we can reuse the same extents. In specific cases, we cache the extents for
 * adjacent collinear walls for windows since windows can span multiple room edges of the same room.
 */
export function getFromOrAddToWallLayerExtentsCache(
  room: Room,
  roomEdgeIndex: number,
  wallLayerExtentsCache: WallLayerExtentsCache,
  roomIndex: RoomIndex,
  wallMassSystem: WallMassSystem,
): WallLayerExtents {
  const roomAndEdgeHash = `${room.id}.${roomEdgeIndex}`;
  const cacheResult = wallLayerExtentsCache[roomAndEdgeHash];

  const wallLayerExtents = cacheResult || getWallLayerExtents(roomIndex, wallMassSystem, room, roomEdgeIndex);

  if (!cacheResult) {
    wallLayerExtentsCache[roomAndEdgeHash] = wallLayerExtents;
  }

  return wallLayerExtents;
}

/**
 * Determines whether an interval, defined by a center param and width, fits within at least one of an array of acceptable intervals.
 */
export function intervalFitsSomewhereInIntervalArray(
  centerParam: number,
  width: number,
  intervalsWhereAnotherCanFit: number[],
): { fits: boolean; paramAdjustmentRequiredToFit: number | null } {
  const domainStart = centerParam - width / 2;
  const domainEnd = centerParam + width / 2;
  const openingWidth = domainEnd - domainStart;

  let fits = false;
  let leastDistanceToWiggleInterval = Number.MAX_VALUE;
  let fitsSomewhere = false;

  for (let i = 0; i < intervalsWhereAnotherCanFit.length - 1; i += 2) {
    const start = intervalsWhereAnotherCanFit[i];
    const end = intervalsWhereAnotherCanFit[i + 1];

    const gapWidth = end - start;
    const wiggleIntervalWidth = gapWidth - openingWidth;
    const wiggleIntervalHalfWidth = wiggleIntervalWidth / 2;
    // is there any wiggleroom (including perfectly fits) of placement
    if (wiggleIntervalWidth + DIST_EPSILON >= 0) {
      fitsSomewhere = true;
      const middleOfGap = start + gapWidth / 2;
      const wiggleIntervalStart = middleOfGap - wiggleIntervalHalfWidth;
      const wiggleIntervalEnd = middleOfGap + wiggleIntervalHalfWidth;

      if (wiggleIntervalStart - DIST_EPSILON <= centerParam && wiggleIntervalEnd + DIST_EPSILON >= centerParam) {
        fits = true;
        return { fits: true, paramAdjustmentRequiredToFit: null };
      } else {
        // this is purposefully positive, since adjustment will be positive
        const distanceToStart = wiggleIntervalStart - centerParam;
        // this is purposefully negative, since adjustment will be negative
        const distanceToEnd = wiggleIntervalEnd - centerParam;

        // if equal distance, prefer start otherwise take least distance
        const startClosest = Math.abs(distanceToStart) <= Math.abs(distanceToEnd);
        const distanceToWiggleInterval = startClosest ? distanceToStart : distanceToEnd;

        if (Math.abs(distanceToWiggleInterval) < Math.abs(leastDistanceToWiggleInterval)) {
          // store negative but always compare on absolute value
          leastDistanceToWiggleInterval = distanceToWiggleInterval;
        }
      }
    }
  }

  if (!fitsSomewhere) {
    return { fits: false, paramAdjustmentRequiredToFit: null };
  }

  return { fits, paramAdjustmentRequiredToFit: leastDistanceToWiggleInterval };
}

/**
 * Expand the last interval in an array of intervals to include an adjacent collinear wall's first interval if the walls have the same
 * layer offsets on both sides. When a window spans multiple room edges, we want to make sure that the window can be installed flush to both
 * room edges outside layers.
 * Also, if a room edge has an intersecting void wall then we use openconnected room sets to find the forward adjacent collinear edges
 * and extend the intervals.
 */
export function extendIntervalsForwardToAdjacentCollinearEdges(
  intervalsWhereAnOpeningCanFit: number[],
  edgeLength: number,
  roomEdgeIndex: number,
  tx: number,
  ty: number,
  room: Room,
  wallLayerExtentsCache: WallLayerExtentsCache,
  roomIndex: RoomIndex,
  wallMassSystem: WallMassSystem,
  lastInsideOffset: number | undefined,
  lastOutsideOffset: number | undefined,
) {
  let currentIntervals = intervalsWhereAnOpeningCanFit;
  let currentStart = 0;
  let currentEnd = edgeLength;
  const { openConnectedRoomSets } = wallMassSystem;
  let associatedRoomSet: RoomSet | undefined = undefined;
  const roomEdgeCount = polyLine2EdgeCount(room.outline);

  const roomEdgeIndexArray: number[] = [];

  for (let i = 1; i < roomEdgeCount; i++) {
    const nextEdgeIndex = (roomEdgeIndex + i) % roomEdgeCount;
    const [nnx, nny] = polyLine2EdgeNormal(room.outline, nextEdgeIndex);

    //collinear check
    if (Math.abs(vec2Dot(tx, ty, nnx, nny)) >= DIST_EPSILON) {
      break;
    }

    roomEdgeIndexArray.push(nextEdgeIndex);
  }

  let finalEdgeEndX = 0;
  let finalEdgeEndY = 0;

  const [roomEdgeStartX, roomEdgeStartY] = polyLine2EdgeStart(room.outline, roomEdgeIndex);
  const [roomEdgeEndX, roomEdgeEndY] = polyLine2EdgeEnd(room.outline, roomEdgeIndex);

  finalEdgeEndX = roomEdgeEndX;
  finalEdgeEndY = roomEdgeEndY;

  for (let i = 0; i < roomEdgeIndexArray.length; i++) {
    const [roomEdgeEndX, roomEdgeEndY] = polyLine2EdgeEnd(room.outline, roomEdgeIndexArray[i]);

    finalEdgeEndX = roomEdgeEndX;
    finalEdgeEndY = roomEdgeEndY;

    const {
      intervalsWhereAnOpeningCanFit: nextIntervals,
      roomEdgeLength: nextEdgeLength,
      firstInsideOffset,
      firstOutsideOffset,
    } = getFromOrAddToWallLayerExtentsCache(
      room,
      roomEdgeIndexArray[i],
      wallLayerExtentsCache,
      roomIndex,
      wallMassSystem,
    );

    /**
     * A check to confirm the wall thickness and offsets are the same between the source wall and this
     * adjacent collinear wall.
     */
    if (
      (firstInsideOffset !== undefined &&
        lastInsideOffset !== undefined &&
        Math.abs(firstInsideOffset - lastInsideOffset) > DIST_EPSILON) ||
      (firstOutsideOffset !== undefined &&
        lastOutsideOffset !== undefined &&
        Math.abs(firstOutsideOffset - lastOutsideOffset) > DIST_EPSILON)
    ) {
      break;
    }

    if (!nextIntervals || nextIntervals.length < 2) {
      break;
    }

    if (currentEnd + nextIntervals[0] - (currentIntervals[currentIntervals.length - 1] + currentStart) > DIST_EPSILON) {
      break;
    }

    if (nextIntervals.length > 2) {
      const newEnd = currentEnd + nextIntervals[1];
      intervalsWhereAnOpeningCanFit[intervalsWhereAnOpeningCanFit.length - 1] = newEnd;
      break;
    } else {
      const newEnd = currentEnd + nextIntervals[1];
      intervalsWhereAnOpeningCanFit[intervalsWhereAnOpeningCanFit.length - 1] = newEnd;

      currentIntervals = nextIntervals;
      currentStart = currentEnd;
      currentEnd += nextEdgeLength;
    }
  }

  const associatedRoomEdgeIndexArray: number[] = [];
  const associatedRoomArray: Room[] = [];

  for (const roomSet of openConnectedRoomSets) {
    if (roomSet.find((r) => r.id === room.id)) {
      //note there will always be one associated room set and will always contain the room itself
      //we filter that out here
      associatedRoomSet = [...roomSet].filter((r) => r.id !== room.id) as RoomSet;
      break;
    }
  }

  // find the forward collinear edges
  if (associatedRoomSet?.length) {
    for (const associatedRoom of associatedRoomSet) {
      const {
        edgeIds,
        level: { id },
      } = associatedRoom;

      if (id !== room.level.id) {
        continue;
      }

      for (const edgeId of edgeIds) {
        const associatedEdgeIndex = edgeIds.indexOf(edgeId);
        const [associatedRoomEdgeStartX, associatedRoomEdgeStartY] = polyLine2EdgeStart(
          associatedRoom.outline,
          associatedEdgeIndex,
        );
        const [associatedRoomEdgeEndX, associatedRoomEdgeEndY] = polyLine2EdgeEnd(
          associatedRoom.outline,
          associatedEdgeIndex,
        );

        //check to see if they are connected
        if (vec2Dist(finalEdgeEndX, finalEdgeEndY, associatedRoomEdgeStartX, associatedRoomEdgeStartY) < DIST_EPSILON) {
          const dx1 = roomEdgeEndX - roomEdgeStartX;
          const dy1 = roomEdgeEndY - roomEdgeStartY;

          const dx2 = associatedRoomEdgeEndX - associatedRoomEdgeStartX;
          const dy2 = associatedRoomEdgeEndY - associatedRoomEdgeStartY;

          const cp = dy2 * dx1 - dx2 * dy1;

          // check to see if they are parallel
          if (Math.abs(cp) < DIST_EPSILON) {
            finalEdgeEndX = associatedRoomEdgeEndX;
            finalEdgeEndY = associatedRoomEdgeEndY;
            associatedRoomEdgeIndexArray.push(associatedEdgeIndex);
            associatedRoomArray.push(associatedRoom);
          }
        }
      }
    }
  }

  for (let i = 0; i < associatedRoomEdgeIndexArray.length; i++) {
    const {
      intervalsWhereAnOpeningCanFit: nextIntervals,
      roomEdgeLength: nextEdgeLength,
      firstInsideOffset,
      firstOutsideOffset,
    } = getFromOrAddToWallLayerExtentsCache(
      associatedRoomArray[i],
      associatedRoomEdgeIndexArray[i],
      wallLayerExtentsCache,
      roomIndex,
      wallMassSystem,
    );

    /**
     * A check to confirm the wall thickness and offsets are the same between the source wall and this
     * adjacent collinear wall.
     */
    if (
      (firstInsideOffset !== undefined &&
        lastInsideOffset !== undefined &&
        Math.abs(firstInsideOffset - lastInsideOffset) > DIST_EPSILON) ||
      (firstOutsideOffset !== undefined &&
        lastOutsideOffset !== undefined &&
        Math.abs(firstOutsideOffset - lastOutsideOffset) > DIST_EPSILON)
    ) {
      break;
    }

    if (!nextIntervals || nextIntervals.length < 2) {
      break;
    }

    if (currentEnd + nextIntervals[0] - (currentIntervals[currentIntervals.length - 1] + currentStart) > DIST_EPSILON) {
      break;
    }

    if (nextIntervals.length > 2) {
      const newEnd = currentEnd + nextIntervals[1];
      intervalsWhereAnOpeningCanFit[intervalsWhereAnOpeningCanFit.length - 1] = newEnd;
      break;
    } else {
      const newEnd = currentEnd + nextIntervals[1];
      intervalsWhereAnOpeningCanFit[intervalsWhereAnOpeningCanFit.length - 1] = newEnd;

      currentIntervals = nextIntervals;
      currentStart = currentEnd;
      currentEnd += nextEdgeLength;
    }
  }
}

/**
 * Expand the first interval in an array of intervals to include an adjacent collinear wall's last interval if the walls have the same
 * layer offsets on both sides. When a window spans multiple room edges, we want to make sure that the window can be installed flush to both
 * room edges outside layers.
 * Also, if a room edge has an intersecting void wall then we use openconnected room sets to find the backward adjacent collinear edges
 * and extend the intervals.
 */
export function extendIntervalsBackwardToAdjacentCollinearEdges(
  intervalsWhereAnOpeningCanFit: number[],
  roomEdgeIndex: number,
  tx: number,
  ty: number,
  room: Room,
  wallLayerExtentsCache: WallLayerExtentsCache,
  roomIndex: RoomIndex,
  wallMassSystem: WallMassSystem,
  firstInsideOffset: number | undefined,
  firstOutsideOffset: number | undefined,
) {
  let currentIntervals = intervalsWhereAnOpeningCanFit;
  let previousStart = 0;
  const { openConnectedRoomSets } = wallMassSystem;
  let associatedRoomSet: RoomSet | null = null;
  const { outline } = room;
  const roomEdgeIndexArray: number[] = [];
  const roomEdgeCount = polyLine2EdgeCount(room.outline);
  for (let i = 1; i < roomEdgeCount; i++) {
    const previousEdgeIndex = (roomEdgeIndex - i + roomEdgeCount) % roomEdgeCount;
    const [nnx, nny] = polyLine2EdgeNormal(outline, previousEdgeIndex);

    //collinear check
    if (Math.abs(vec2Dot(tx, ty, nnx, nny)) >= DIST_EPSILON) {
      break;
    }

    roomEdgeIndexArray.push(previousEdgeIndex);
  }

  let finalEdgeStartX = 0;
  let finalEdgeStartY = 0;

  const [roomEdgeStartX, roomEdgeStartY] = polyLine2EdgeStart(room.outline, roomEdgeIndex);
  const [roomEdgeEndX, roomEdgeEndY] = polyLine2EdgeEnd(room.outline, roomEdgeIndex);

  finalEdgeStartX = roomEdgeStartX;
  finalEdgeStartY = roomEdgeStartY;

  for (let i = 0; i < roomEdgeIndexArray.length; i++) {
    const [roomEdgeStartX, roomEdgeStartY] = polyLine2EdgeStart(room.outline, roomEdgeIndexArray[i]);

    finalEdgeStartX = roomEdgeStartX;
    finalEdgeStartY = roomEdgeStartY;

    const {
      intervalsWhereAnOpeningCanFit: previousIntervals,
      roomEdgeLength: previousRoomEdgeLength,
      lastInsideOffset,
      lastOutsideOffset,
    } = getFromOrAddToWallLayerExtentsCache(
      room,
      roomEdgeIndexArray[i],
      wallLayerExtentsCache,
      roomIndex,
      wallMassSystem,
    );
    previousStart += previousRoomEdgeLength;

    /**
     * A check to confirm the wall thickness and offsets are the same between the source wall and this
     * adjacent collinear wall.
     */
    if (
      (lastInsideOffset !== undefined &&
        firstInsideOffset !== undefined &&
        Math.abs(firstInsideOffset - lastInsideOffset) > DIST_EPSILON) ||
      (lastOutsideOffset !== undefined &&
        firstOutsideOffset !== undefined &&
        Math.abs(firstOutsideOffset - lastOutsideOffset) > DIST_EPSILON)
    ) {
      break;
    }

    if (!previousIntervals || previousIntervals.length < 2) {
      break;
    }

    if (previousIntervals[previousIntervals.length - 1] - previousStart - currentIntervals[0] > DIST_EPSILON) {
      break;
    }

    if (previousIntervals.length > 2) {
      const newStart = previousIntervals[previousIntervals.length - 2] - previousStart;
      intervalsWhereAnOpeningCanFit[0] = newStart;
      break;
    } else {
      const newStart = previousIntervals[previousIntervals.length - 2] - previousStart;
      intervalsWhereAnOpeningCanFit[0] = newStart;
      currentIntervals = previousIntervals;
    }
  }

  const associatedRoomEdgeIndexArray: number[] = [];
  const associatedRoomArray: Room[] = [];

  for (const roomSet of openConnectedRoomSets) {
    if (roomSet.find((r) => r.id === room.id)) {
      //note there will always be one associated room set and will always contain the room itself
      //we filter that out here
      associatedRoomSet = [...roomSet].filter((r) => r.id !== room.id) as RoomSet;
      break;
    }
  }

  //find the backward collinear edges
  if (associatedRoomSet?.length) {
    for (const associatedRoom of associatedRoomSet) {
      const {
        edgeIds,
        level: { id },
      } = associatedRoom;

      if (id !== room.level.id) {
        continue;
      }

      for (const edgeId of edgeIds) {
        const associatedEdgeIndex = edgeIds.indexOf(edgeId);
        const [associatedRoomEdgeStartX, associatedRoomEdgeStartY] = polyLine2EdgeStart(
          associatedRoom.outline,
          associatedEdgeIndex,
        );
        const [associatedRoomEdgeEndX, associatedRoomEdgeEndY] = polyLine2EdgeEnd(
          associatedRoom.outline,
          associatedEdgeIndex,
        );

        if (vec2Dist(finalEdgeStartX, finalEdgeStartY, associatedRoomEdgeEndX, associatedRoomEdgeEndY) < DIST_EPSILON) {
          const dx1 = roomEdgeEndX - roomEdgeStartX;
          const dy1 = roomEdgeEndY - roomEdgeStartY;

          const dx2 = associatedRoomEdgeEndX - associatedRoomEdgeStartX;
          const dy2 = associatedRoomEdgeEndY - associatedRoomEdgeStartY;

          const cp = dy2 * dx1 - dx2 * dy1;

          // check to see if they are parallel
          if (Math.abs(cp) < DIST_EPSILON) {
            finalEdgeStartX = associatedRoomEdgeStartX;
            finalEdgeStartY = associatedRoomEdgeStartY;
            associatedRoomEdgeIndexArray.push(associatedEdgeIndex);
            associatedRoomArray.push(associatedRoom);
          }
        }
      }
    }
  }

  for (let i = 0; i < associatedRoomEdgeIndexArray.length; i++) {
    const {
      intervalsWhereAnOpeningCanFit: previousIntervals,
      roomEdgeLength: previousRoomEdgeLength,
      lastInsideOffset,
      lastOutsideOffset,
    } = getFromOrAddToWallLayerExtentsCache(
      associatedRoomArray[i],
      associatedRoomEdgeIndexArray[i],
      wallLayerExtentsCache,
      roomIndex,
      wallMassSystem,
    );
    previousStart += previousRoomEdgeLength;

    /**
     * A check to confirm the wall thickness and offsets are the same between the source wall and this
     * adjacent collinear wall.
     */
    if (
      (lastInsideOffset !== undefined &&
        firstInsideOffset !== undefined &&
        Math.abs(firstInsideOffset - lastInsideOffset) > DIST_EPSILON) ||
      (lastOutsideOffset !== undefined &&
        firstOutsideOffset !== undefined &&
        Math.abs(firstOutsideOffset - lastOutsideOffset) > DIST_EPSILON)
    ) {
      break;
    }

    if (!previousIntervals || previousIntervals.length < 2) {
      break;
    }

    if (previousIntervals[previousIntervals.length - 1] - previousStart - currentIntervals[0] > DIST_EPSILON) {
      break;
    }

    if (previousIntervals.length > 2) {
      const newStart = previousIntervals[previousIntervals.length - 2] - previousStart;
      intervalsWhereAnOpeningCanFit[0] = newStart;
      break;
    } else {
      const newStart = previousIntervals[previousIntervals.length - 2] - previousStart;
      intervalsWhereAnOpeningCanFit[0] = newStart;
      currentIntervals = previousIntervals;
    }
  }
}
